import { ISettingsProps } from "../Models.ts";
import {
    AlgebraQuestionOptions,
    isRationalQuestion,
    ignoresNumberPropertyMaxValue,
    isScientificQuestion,
    MissingNumberOptions,
    Operations,
    ProblemFormatOptions,
    RationalKindOptions,
    RegroupingOptions,
    StoryTypeOptions,
} from "../Constants.ts";
import { usableSections } from "../SpiralReviewGenerator.ts";
import { levelApplies } from "../FactLevels.ts";

/**
 * The advanced tier of the settings panel (ADR 0023).
 *
 * Two things live here, and they live together on purpose:
 *
 * 1. One predicate per advanced control, saying whether that control applies to
 *    the sheet as configured. A carry box above a clock face is meaningless, and
 *    the worked key does nothing without answer pages.
 * 2. `advancedSettingsInUse`, which asks which of those controls are both
 *    applicable and set to something other than their default.
 *
 * (2) has to agree with (1) exactly - a setting that forces the panel open must
 * be one the open panel actually renders - so the conditions are stated once and
 * imported by the components that render the controls, rather than being written
 * out in each of them and again here. See the `footerGroupOf()` warning in
 * CLAUDE.md for what happens when a pair like this drifts.
 *
 * The reverse does not hold: not everything the panel hides belongs in (2).
 * The ADR 0023 amendment put four controls behind the button while leaving them
 * out of ADVANCED - the problem format, "New problems", "Rebuild" and the class
 * roster. ADR 0061 §3 took all four back out of the advanced tier: the format
 * picker is a one-row select in the basic tier, the seed controls sit beside
 * the sheet, and the roster sits under the rail. None of them was ever in (2),
 * and none needs to be now that none is hidden.
 *
 * The rule that matters is the one about silence: an advanced control may be
 * left out of (2) only when its effect is visible somewhere else on the page.
 */

function operators(settings: ISettingsProps): Operations[] {
    return settings.Operators?.map((operator) => operator.value) ?? [];
}

function isMathFacts(settings: ISettingsProps): boolean {
    return settings.Format === ProblemFormatOptions.MathFacts;
}

/**
 * The formats whose problems come from the operand-pair path in
 * ProblemGenerator (`generateProblems`), the one place `Regrouping` is read.
 * Color by number is here because it asks that path for a plain fact sheet
 * with the sheet's own settings (ADR 0058 §3).
 */
const REGROUPING_FORMATS: ProblemFormatOptions[] = [
    ProblemFormatOptions.MathFacts,
    ProblemFormatOptions.WordProblem,
    ProblemFormatOptions.ColorByNumber,
    // The decoder's problems are the same plain facts (ADR 0088 §3).
    ProblemFormatOptions.RiddleDecoder,
];

/**
 * Regrouping is an addition and subtraction idea, and only means anything on a
 * sheet whose problems are operand pairs. Every other format - fractions,
 * money, decimals, algebra, a fact family, place value, a clock - has its own
 * generator that never reads the setting, so the control would change nothing
 * there. A spiral review applies it through its math-facts section, which
 * inherits the sheet's settings (ADR 0013), so it counts when it has one.
 */
export function showsRegrouping(settings: ISettingsProps): boolean {
    const ops = operators(settings);
    if (!ops.includes(Operations.Addition) && !ops.includes(Operations.Subtraction)) {
        return false;
    }
    if (settings.Format === ProblemFormatOptions.SpiralReview) {
        return usableSections(settings.SpiralSections ?? [])
            .some((section) => section.format === ProblemFormatOptions.MathFacts);
    }
    return REGROUPING_FORMATS.includes(settings.Format);
}

/**
 * The angle step is a Geometry control (ADR 0049 §6). Advanced, not basic, on
 * ADR 0023 §2's test: it changes how hard the sheet is, not what it asks.
 */
export function showsGeometryAngleStep(settings: ISettingsProps): boolean {
    return settings.Format === ProblemFormatOptions.Geometry;
}

/**
 * Only long division prints a remainder - and never on a color-by-number
 * sheet, whose colours are keyed to single numbers (ADR 0058 §3).
 */
export function showsRemainders(settings: ISettingsProps): boolean {
    return (
        operators(settings).includes(Operations.LongDivision) &&
        settings.Format !== ProblemFormatOptions.ColorByNumber &&
        settings.Format !== ProblemFormatOptions.RiddleDecoder
    );
}

/**
 * A 168-cell picture has no half-page size (ADR 0058 §5), and neither does a
 * spiral review: four sections in half a page is one problem each (ADR 0013
 * §4). The spiral half of this was missing until the page's copy, which says
 * it is not offered, was checked against the panel.
 */
export function showsHalfPage(settings: ISettingsProps): boolean {
    return (
        settings.Format !== ProblemFormatOptions.ColorByNumber &&
        settings.Format !== ProblemFormatOptions.RiddleDecoder &&
        settings.Format !== ProblemFormatOptions.SpiralReview
    );
}

/**
 * Missing number applies to all four operations, so it is shown whenever the
 * sheet is math facts - except long division, whose bracket layout has nowhere
 * to put a blank operand (ADR 0005 §5).
 */
export function showsMissingNumber(settings: ISettingsProps): boolean {
    return isMathFacts(settings) && !operators(settings).includes(Operations.LongDivision);
}

/**
 * The worksheet language picker is **hidden for now** - the setting still
 * works, it simply has no control.
 *
 * `Language` is untouched: `lang=` still decodes, the localised routes still
 * pin their own language and still print Spanish and French worksheets, and
 * everything ADR 0051 built is still wired up. This is the one predicate that
 * decides whether a reader is offered the choice, and it is the whole of the
 * hiding - `SettingsContainer` renders the control through it, and
 * `advancedSettingsInUse` filters on `applies(settings) && inUse(settings)`,
 * so a route that pins a non-English language no longer forces the advanced
 * panel open for a control that would not be there. That pairing is why this
 * is one line rather than several, and why the two halves cannot drift apart
 * while it is hidden.
 *
 * To bring it back, return true - which is what ADR 0051 part 3 widened it to,
 * from the word-problem-only test it had before that.
 */
export function showsLanguage(_settings: ISettingsProps): boolean {
    return false;
}

/**
 * Varied stories (ADR 0073 section 2) apply to a word-problem sheet with an operation that has new
 * story types: addition, subtraction or division. A multiplication-only sheet has none, and the
 * long-division page tells remainder stories, which Varied does not touch, so the control would
 * change nothing there.
 */
export function showsStoryTypes(settings: ISettingsProps): boolean {
    if (settings.Format !== ProblemFormatOptions.WordProblem) {
        return false;
    }
    return operators(settings).some(
        (operator) =>
            operator === Operations.Addition ||
            operator === Operations.Subtraction ||
            operator === Operations.Division
    );
}

/**
 * Fact fluency levels (ADR 0071 §5) apply to a math-facts sheet of the four
 * fact operations - not long division, which has no ladder. Deliberately not
 * "a ladder exists": on a sheet of + and x the toggle still shows, so the
 * level picker can say why the level changed nothing.
 */
export function showsFactLevels(settings: ISettingsProps): boolean {
    return isMathFacts(settings) && !operators(settings).includes(Operations.LongDivision);
}

/**
 * Whether this sheet is the home practice for a fact fluency level (ADR 0071 §8): a level
 * sheet that asks to be one. Read here, in Pages and in the level picker through this one
 * function, so none of them can disagree.
 */
export function isPracticeSheet(settings: ISettingsProps): boolean {
    return settings.FactLevelSheet === "practice" && levelApplies(settings);
}

/**
 * The Timed test toggle applies to every sheet but a home practice sheet, whose header has no
 * clock (ADR 0071 §8). A toggle that changes nothing must not show, and a stray `td=1` on a
 * practice sheet must not open the panel (the 0023 pairing rule).
 */
export function showsTimed(settings: ISettingsProps): boolean {
    return !isPracticeSheet(settings);
}

/** Carry boxes: columns that carry, which is addition and subtraction (ADR 0014 §2). */
export function showsCarryBoxes(settings: ISettingsProps): boolean {
    const ops = operators(settings);
    return (
        isMathFacts(settings) &&
        (ops.includes(Operations.Addition) || ops.includes(Operations.Subtraction))
    );
}

/** The alignment grid is for the algorithms with partial rows to line up. */
export function showsAlignmentGrid(settings: ISettingsProps): boolean {
    const ops = operators(settings);
    return (
        isMathFacts(settings) &&
        (ops.includes(Operations.Multiplication) || ops.includes(Operations.LongDivision))
    );
}

/** Large print applies to any arithmetic format. */
export function showsLargePrint(settings: ISettingsProps): boolean {
    return (
        settings.Format === ProblemFormatOptions.MathFacts ||
        settings.Format === ProblemFormatOptions.Money ||
        settings.Format === ProblemFormatOptions.Decimals
    );
}

/** Whether any of the three layout scaffolds applies, i.e. whether that row renders. */
export function showsPresentation(settings: ISettingsProps): boolean {
    return showsCarryBoxes(settings) || showsAlignmentGrid(settings) || showsLargePrint(settings);
}

/**
 * The worked key only does something for the formats WorkedSteps.ts covers, and
 * only when there is a key to write it on (ADR 0015 §2).
 */
/**
 * Only two formats can take negatives, and both are horizontal - which is
 * exactly why they are the two that can (ADR 0036 §2, and ADR 0030 §2's
 * containment boundary for why the column formats cannot).
 *
 * Writing Expressions is the one Algebra type that never prints a negative
 * number (ADR 0123 §1), so the control is hidden there rather than left to do
 * nothing.
 */
export function showsAllowNegatives(settings: ISettingsProps): boolean {
    return (
        (settings.Format === ProblemFormatOptions.Algebra &&
            settings.AlgebraQuestion !== AlgebraQuestionOptions.WritingExpressions) ||
        settings.Format === ProblemFormatOptions.OrderOfOperations
    );
}

/**
 * Decimals, fractions or both is a basic-tier control, not an advanced one:
 * which numbers are on the sheet is the first thing a teacher chooses, and on
 * this format the panel is the only way to choose it (ADR 0122 §2, ADR 0030 §3).
 * It lives here with the other applicability predicates, so the panel imports it
 * rather than restating it. Only the three rational question types read it.
 */
export function showsRationalKind(settings: ISettingsProps): boolean {
    return (
        settings.Format === ProblemFormatOptions.Integers &&
        isRationalQuestion(settings.IntegerQuestion)
    );
}

/**
 * The integer "largest number" dial. Hidden when a rational type is set to
 * fractions only: fractions ignore it, their denominators run from 2 to 12
 * (ADR 0122 §5), and a dial that changes nothing is a control that lies.
 */
export function showsIntegerMaxValue(settings: ISettingsProps): boolean {
    return !(showsRationalKind(settings) && settings.RationalKind === RationalKindOptions.Fractions);
}

/**
 * The number-property "largest number" dial. Hidden for the three root types
 * (ADR 0132 §3): their radicands come from a fixed list of roots, and a dial
 * that changes nothing is a control that lies. Hidden for Exponent Rules too
 * (ADR 0136 §6), whose bases and exponents are fixed ranges.
 */
export function showsNumberPropertyMaxValue(settings: ISettingsProps): boolean {
    return !ignoresNumberPropertyMaxValue(settings.NumberPropertyQuestion);
}

/**
 * The place-value digits control. Hidden for the two scientific-notation types
 * (ADR 0130 §1): their numbers come from a fixed recipe of coefficient and
 * exponent, and a control that changes nothing is a control that lies.
 */
export function showsPlaceValueDigits(settings: ISettingsProps): boolean {
    return !isScientificQuestion(settings.PlaceValueQuestion);
}

export function showsWorkedAnswers(settings: ISettingsProps): boolean {
    return (
        settings.AnswerPages &&
        (settings.Format === ProblemFormatOptions.MathFacts ||
            settings.Format === ProblemFormatOptions.Fractions)
    );
}

/**
 * Every advanced control, with the condition under which it applies and the
 * test for "the visitor has changed this from its default".
 *
 * The labels are the ones the controls carry, so a future summary line can name
 * them without a second vocabulary.
 */
const ADVANCED: {
    label: string;
    applies: (settings: ISettingsProps) => boolean;
    inUse: (settings: ISettingsProps) => boolean;
}[] = [
    {
        label: "Regrouping",
        applies: showsRegrouping,
        inUse: (s) => s.Regrouping !== RegroupingOptions.Both,
    },
    {
        label: "Remainders",
        applies: showsRemainders,
        inUse: (s) => s.Remainders === true,
    },
    {
        label: "Missing number",
        applies: showsMissingNumber,
        inUse: (s) => s.MissingNumber !== MissingNumberOptions.Off,
    },
    {
        label: "Worksheet language",
        applies: showsLanguage,
        inUse: (s) => (s.Language ?? "en") !== "en",
    },
    {
        label: "Angle step",
        applies: showsGeometryAngleStep,
        inUse: (s) => (s.GeometryAngleStep ?? 5) !== 5,
    },
    {
        label: "Carry boxes",
        applies: showsCarryBoxes,
        inUse: (s) => s.Presentation?.carryBoxes === true,
    },
    {
        label: "Alignment grid",
        applies: showsAlignmentGrid,
        inUse: (s) => s.Presentation?.alignmentGrid === true,
    },
    {
        label: "Large print",
        applies: showsLargePrint,
        inUse: (s) => s.Presentation?.largePrint === true,
    },
    // Timed test and the differentiation set apply to every sheet; half page
    // to every sheet but a color-by-number picture.
    {
        label: "Negative numbers",
        applies: showsAllowNegatives,
        inUse: (s) => s.AllowNegatives === true,
    },
    { label: "Half page", applies: showsHalfPage, inUse: (s) => s.HalfPage === true },
    { label: "Timed test", applies: showsTimed, inUse: (s) => s.Timed === true },
    {
        label: "Story types",
        applies: showsStoryTypes,
        inUse: (s) => s.StoryTypes === StoryTypeOptions.Varied,
    },
    {
        label: "Fact levels",
        applies: showsFactLevels,
        inUse: (s) => (s.FactLevel ?? 0) > 0,
    },
    {
        label: "Show the working",
        applies: showsWorkedAnswers,
        inUse: (s) => s.WorkedAnswers === true,
    },
    {
        label: "Differentiation set",
        applies: () => true,
        inUse: (s) => s.DifferentiationSet === true,
    },
];

/**
 * The advanced settings that are both applicable to this sheet and set to
 * something other than their default, by label.
 *
 * Used for two things (ADR 0023 §5): opening the panel on load, so a route that
 * pins an advanced setting or a shared link that carries one does not hide the
 * control that explains the worksheet; and counting them on the button when a
 * visitor collapses the panel with settings still on.
 *
 * Applicability is half the question deliberately. A stray `r=1` in a URL must
 * not force the panel open on an addition sheet, which would not render the
 * remainder toggle even when open.
 */
export function advancedSettingsInUse(settings: ISettingsProps): string[] {
    return ADVANCED.filter((entry) => entry.applies(settings) && entry.inUse(settings)).map(
        (entry) => entry.label
    );
}
